import { useNavigate } from 'react-router-dom';
import {
  Settings,
  LogOut,
  ChevronDown,
  Building2,
  ArrowRight,
  BarChart3,
  LayoutDashboard,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useAuth } from '@/hooks/useAuth';
import { useState } from 'react';
import { SettingsModal } from '@/components/settings/SettingsModal';
import { hasFullAppAccess } from '@/lib/authRedirect';

interface UserMenuButtonProps {
  variant?: 'funcionarios' | 'oposiciones';
}

export function UserMenuButton({ variant = 'funcionarios' }: UserMenuButtonProps) {
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : profile?.email?.slice(0, 2).toUpperCase() || 'U';

  const isBetaTester = hasFullAppAccess(profile);
  const isSuperAdmin = profile?.role === 'super_admin';

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth/login');
  };

  const handleSwitchMode = () => {
    if (variant === 'oposiciones') {
      navigate('/app/chat');
    } else {
      navigate('/app/oposiciones/chat');
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1 p-1.5 shrink-0">
            <Avatar className="h-7 w-7">
              <AvatarFallback className="bg-violet-100 text-violet-700 text-xs">
                {initials}
              </AvatarFallback>
            </Avatar>
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <div className="px-2 py-1.5">
            <p className="text-sm font-medium">{profile?.full_name || 'Usuario'}</p>
            <p className="text-xs text-muted-foreground">{profile?.email}</p>
          </div>
          <DropdownMenuSeparator />

          {isSuperAdmin && (
            <>
              <DropdownMenuItem 
                onClick={() => navigate('/admin/dashboard')}
                className="text-primary"
              >
                <LayoutDashboard className="h-4 w-4 mr-2" />
                Panel Admin
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          <DropdownMenuItem onClick={() => setSettingsOpen(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Configuración
          </DropdownMenuItem>

          {variant === 'oposiciones' && (
            <DropdownMenuItem onClick={() => navigate('/app/oposiciones/progreso')}>
              <BarChart3 className="h-4 w-4 mr-2" />
              Mi progreso
            </DropdownMenuItem>
          )}

          {isBetaTester && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleSwitchMode}
                className="bg-primary/5"
              >
                <Building2 className="h-4 w-4 mr-2 text-primary" />
                <span className="flex-1">
                  {variant === 'oposiciones' ? 'Cambiar a Funcionarios' : 'Cambiar a Oposiciones'}
                </span>
                <ArrowRight className="h-3.5 w-3.5 text-primary" />
              </DropdownMenuItem>
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
            <LogOut className="h-4 w-4 mr-2" />
            Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <SettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />
    </>
  );
}
